import React from 'react';
import { Link } from 'react-router';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { Points } from 'components/Points/Points';
import RankTierBadge from '../RankTierBadge';
import { LeaderboardEntry, LeaderboardCategory, CATEGORY_CONFIG } from 'types/interfaces/leaderboard';
import './LeaderboardTable.css';

interface LeaderboardTableProps {
  rankings: LeaderboardEntry[];
  category: LeaderboardCategory;
  isLoading?: boolean;
  currentUserId?: number | null;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
}

const LeaderboardTable: React.FC<LeaderboardTableProps> = ({
  rankings,
  category,
  isLoading,
  currentUserId,
  onLoadMore,
  hasMore,
  loadingMore,
}) => {
  // Hide stat column when it duplicates a dedicated column (Points or Wants)
  const hideStatColumn = category === 'new_members' || category === 'most_points';
  const statLabel = CATEGORY_CONFIG[category].statLabel;

  const formatDisplayValue = (value: number | string, cat: LeaderboardCategory): string => {
    if (typeof value === 'string') return value;

    if (cat === 'highest_rated') {
      return value.toFixed(1);
    }
    if (cat === 'rising_stars') {
      return `+${value}%`;
    }
    return value.toLocaleString();
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="leaderboard-table">
        <div className="leaderboard-table__loading">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="leaderboard-table__skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (rankings.length === 0) {
    return (
      <div className="leaderboard-table leaderboard-table--empty">
        <p>No traders found for this category.</p>
      </div>
    );
  }

  return (
    <div className={`leaderboard-table ${hideStatColumn ? 'leaderboard-table--no-stat' : ''}`}>
      <div className="leaderboard-table__header">
        <div className="leaderboard-table__col leaderboard-table__col--rank">Rank</div>
        <div className="leaderboard-table__col leaderboard-table__col--user">Member</div>
        <div className="leaderboard-table__col leaderboard-table__col--points">Points</div>
        <div className="leaderboard-table__col leaderboard-table__col--tier">Tier</div>
        {!hideStatColumn && (
          <div className="leaderboard-table__col leaderboard-table__col--stat">{statLabel}</div>
        )}
        <div className="leaderboard-table__col leaderboard-table__col--wants">Wants</div>
        <div className="leaderboard-table__col leaderboard-table__col--joined">Joined</div>
      </div>

      <div className="leaderboard-table__body">
        {rankings.map((entry) => (
          <Link
            key={entry.id}
            to={`/profile/u/${encodeURIComponent(entry.username)}`}
            className={`leaderboard-table__row ${currentUserId === entry.id ? 'leaderboard-table__row--current-user' : ''}`}
          >
            <div className="leaderboard-table__col leaderboard-table__col--rank">
              #{entry.rank}
            </div>
            <div className="leaderboard-table__col leaderboard-table__col--user">
              <AvatarWithFlag
                avatarUrl={entry.avatarUrl}
                username={entry.username}
                size={36}
                countryCode={entry.country}
              />
              <span className="leaderboard-table__username">{entry.username}</span>
            </div>
            <div className="leaderboard-table__col leaderboard-table__col--points">
              <Points points={entry.points} type="small" />
            </div>
            <div className="leaderboard-table__col leaderboard-table__col--tier">
              <RankTierBadge tier={entry.tier} size="small" />
            </div>
            {!hideStatColumn && (
              <div className="leaderboard-table__col leaderboard-table__col--stat">
                {formatDisplayValue(entry.displayValue, category)}
              </div>
            )}
            <div className="leaderboard-table__col leaderboard-table__col--wants">
              {entry.wantListCount?.toLocaleString() ?? '—'}
            </div>
            <div className="leaderboard-table__col leaderboard-table__col--joined">
              {formatDate(entry.memberSince)}
            </div>
          </Link>
        ))}
      </div>

      {hasMore && (
        <div className="leaderboard-table__load-more">
          <button
            className="leaderboard-table__load-more-btn"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default LeaderboardTable;
